import React, { PropsWithChildren } from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { AdditionalPrivilegeCheckBoxParam } from './AdditionalPrivilegeConfigEditForm';
import { PrivilegeCheckboxTreeNode } from './PrivilegeCheckboxTreeNode';

type PrivilegeCheckboxTreeProps = {
  privilegeParams: AdditionalPrivilegeCheckBoxParam[];

  /**
   * Function to call when any of the privilege checkboxes in this (sub)tree becomes checked.
   */
  onAnyChildPrivilegeChecked?: () => void;
  ref?: React.RefObject<HTMLUListElement | null>;
};

/**
 * Renders a tree of checkboxes for the selection of privileges and their dependent privileges.
 * Unchecking a privilege will uncheck all dependent privileges (nested child checkboxes).
 * Checking a privilege also checks all required privileges (checkboxes of the parent tree nodes).
 */
export function PrivilegeCheckboxTree({
  privilegeParams,
  onAnyChildPrivilegeChecked,
  ref,
}: PropsWithChildren<PrivilegeCheckboxTreeProps>) {
  const { t } = useSonTranslation('PrivilegeConfig');

  // sort the privileges of each tree node by the translated privilege title:
  const sortedPrivilegeParams = privilegeParams
    .map(privilegeParam => ({
      ...privilegeParam,
      label: t(`input.${privilegeParam.value?.toLowerCase()}.label` as any),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <ul ref={ref} className="privilege-config__additional-privilege-checkbox-tree-list">
      {sortedPrivilegeParams.map(privilegeParam => (
        <li key={privilegeParam.value} className="privilege-config__additional-privilege-checkbox-tree-list-item">
          <PrivilegeCheckboxTreeNode
            privilegeFormParam={privilegeParam}
            onPrivilegeChecked={onAnyChildPrivilegeChecked}
          />
        </li>
      ))}
    </ul>
  );
}
